import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { MaskLines } from "@/components/Reveal";
import { IMAGES } from "@/lib/content";

export default function Hero() {
  const { t } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section id="home" ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden pt-24 pb-16">
      {/* Background image with parallax */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 z-0 scale-110">
        <img src={IMAGES.hero} alt="Warmes, helles Wohnzimmer" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-[1] bg-gradient-to-t from-black/95 via-black/55 to-black/30" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black/75 via-black/20 to-transparent" />
      <div className="absolute inset-0 z-[1] bg-[radial-gradient(60%_50%_at_50%_100%,rgba(248,132,37,0.18),transparent)]" />

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-ibk" />
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-white/90" data-testid="hero-kicker">{t.hero.kicker}</span>
        </motion.div>

        <h1 className="font-serif text-white text-[clamp(3rem,10vw,9rem)] leading-[0.92] tracking-tight font-medium" data-testid="hero-title">
          <MaskLines
            lines={[t.hero.l1]}
            lineClass=""
          />
          <span className="block">
            <MaskLines
              lines={[<span key="x">{t.hero.l2} <span className="italic text-ibk">{t.hero.l3}</span></span>]}
              startDelay={0.28}
            />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a
            href="#contact"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-ibk px-8 py-4 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.03]"
          >
            {t.hero.cta}
            <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/60 [writing-mode:vertical-rl]">scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={16} className="text-ibk" />
        </motion.span>
      </motion.div>
    </section>
  );
}
